import { pick } from "lodash";

import { cloneError } from "./clone-error";
import { isPHIValue } from "./phi-value";

export const TRUNCATED = "[TRUNCATED]";
export const REDACTED = "[REDACTED]";
export const REDACTED_PHI = "[REDACTED_PHI]";

const SENSITIVE_KEYS = [
  "password",
  "token",
  "apiKey",
  "apiSecret",
  "encryptionSecret",
  "secret",
  "key",
  "credential",
  "accessToken",
  "refreshToken",
  "authToken",
  "auth",
  "jwt",
  "private",
  "certificate",
  "passphrase",
  "passcode",
  "pin",
  "ssn",
  "socialSecurity",
  "creditCard",
  "ccNumber",
  "cvv",
  "cardNumber",
];

export const EMAIL_REGEX =
  /(?<!\w)(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})(?!\w)/gi;
export const PHONE_REGEX_PATTERNS = [
  // US phone with country code and formatting: +1 (555) 123-4567, +1(222) 333-4444
  /(?<!\d)\+1\s*\(?\d{3}\)?\s*[-.\s]\d{3}[-.\s]\d{4}(?!\d)/gi,
  // US phone with separators: 555-123-4567, 555.123.4567, 555 123 4567
  /(?<!\d)\d{3}[-.\s]\d{3}[-.\s]\d{4}(?!\d)/gi,
  // US phone with parentheses: (555) 123-4567
  /(?<!\d)\(\d{3}\)\s*\d{3}[-.\s]\d{4}(?!\d)/gi,
  // 11-digit with country code but no separators: +12223334444
  /(?<!\d)\+1\d{10}(?!\d)/gi,
  // 11-digit starting with 1 (no separators): 12223334444
  /(?<!\d)1\d{10}(?!\d)/gi,
  // 11-digit with separators starting with 1: 1-222-333-4444, 1.222.333.4444, 1 222 333 4444
  /(?<!\d)1[-.\s]\d{3}[-.\s]\d{3}[-.\s]\d{4}(?!\d)/gi,
];

/**
 * Redacts sensitive data from the given thing. This should be called
 * before logging anything, sending to error reporting (Sentry, Datadog),
 * or any other non-PHI services (non-PHI event buses, other third party services, etc).
 *
 * By default it will redact:
 * - any detected PHI objects
 * - particularly sensitive keys (e.g. passwords, tokens, etc)
 *
 * If opted in via `opts`:
 * - emails and phone numbers
 */
export function redactThing(
  thing: any,
  opts: { redactEmails?: boolean; redactPhoneNumbers?: boolean } = {},
  depth: number = 0
): any {
  if (depth > 9) {
    return TRUNCATED;
  }

  if (thing instanceof Error) {
    // clone error to normalize the error object,
    // because sometimes there is a getter on the error object,
    // which will cause the error to be not serializable properly
    const redactedError = cloneError(thing);

    // redact non-enumerable error properties
    redactedError.message = redactThing(redactedError.message, opts);
    redactedError.cause = redactedError.cause
      ? redactThing(redactedError.cause, opts, depth + 1)
      : undefined;

    // redact enumerable error properties
    const enumerableProps = pick(redactedError, Object.keys(redactedError));
    Object.assign(redactedError, redactThing(enumerableProps, opts, depth));

    return redactedError;
  }

  if (Array.isArray(thing)) {
    return thing.map((item) => redactThing(item, opts, depth + 1));
  }

  if (typeof thing === "object" && thing !== null) {
    if (isPHIValue(thing)) {
      return REDACTED_PHI;
    }

    return Object.fromEntries(
      Object.entries(thing).map(([key, value]) => [
        key,
        // Redact sensitive keys
        isSensitiveKey(key) ? REDACTED : redactThing(value, opts, depth + 1),
      ])
    );
  }

  if (typeof thing === "string") {
    return redactStringWithUUIDPreservation(thing, opts);
  }

  if (typeof thing === "function") {
    // here we are dealing with custom/overidden functions only
    return null;
  }

  // primitive value other than function or string, will be logged as is
  return thing;
}

function redactStringWithUUIDPreservation(
  value: string,
  opts: { redactEmails?: boolean; redactPhoneNumbers?: boolean }
): string {
  const UUID_REGEX =
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

  // Find all UUIDs in the string and create a map to preserve them
  const uuids: string[] = [];
  const placeholderMap = new Map<string, string>();

  // Replace UUIDs with temporary placeholders
  let processedString = value.replace(UUID_REGEX, (match) => {
    const placeholder = `__UUID_PLACEHOLDER_${uuids.length}__`;
    uuids.push(match);
    placeholderMap.set(placeholder, match);
    return placeholder;
  });

  // Apply email and phone redaction to the string with placeholders
  if (opts.redactEmails) {
    processedString = processedString.replace(EMAIL_REGEX, REDACTED);
  }

  if (opts.redactPhoneNumbers) {
    // Apply each phone regex pattern
    PHONE_REGEX_PATTERNS.forEach((pattern) => {
      processedString = processedString.replace(pattern, REDACTED);
    });
  }

  // Restore the original UUIDs
  placeholderMap.forEach((originalUuid, placeholder) => {
    processedString = processedString.replace(placeholder, originalUuid);
  });

  return processedString;
}

/**
 * Determines if the given key is sensitive and should be redacted.
 *
 * This includes specific sensitive keys, as well as keys that start with "phi"
 * (which are PHI values).
 */
function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEYS.includes(key) || key.startsWith("phi");
}
